import React from 'react';
import { FaTimes, FaChevronDown, FaCheck } from 'react-icons/fa';

interface InputProps {
  label: React.ReactNode;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  min?: number;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  min,
  className = '',
  disabled = false,
  icon,
}) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
      {icon && <span className="text-blue-500">{icon}</span>}
      {label}
    </label>
    <div className="relative">
      <input
        type={type}
        min={min}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`
          w-full px-4 py-3 border-2 border-gray-200 rounded-xl
          focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
          hover:border-gray-300 transition-all duration-200
          placeholder:text-gray-400 text-gray-900
          ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
          ${className}
        `}
        disabled={disabled}
      />
    </div>
  </div>
);

interface TextAreaProps {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  icon?: React.ReactNode;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  className = '',
  icon,
}) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
      {icon && <span className="text-blue-500">{icon}</span>}
      {label}
    </label>
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      className={`
        w-full px-4 py-3 border-2 border-gray-200 rounded-xl
        focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
        hover:border-gray-300 transition-all duration-200
        placeholder:text-gray-400 text-gray-900 bg-white resize-none
        ${className}
      `}
    />
  </div>
);

interface SelectProps {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  className = '',
  disabled = false,
  icon,
}) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
      {icon && <span className="text-blue-500">{icon}</span>}
      {label}
    </label>
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`
          appearance-none w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl bg-white
          focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
          hover:border-gray-300 transition-all duration-200 text-gray-900
          ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `}
        disabled={disabled}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2">
        <FaChevronDown className="text-gray-400" />
      </div>
    </div>
  </div>
);

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  disabled?: boolean;
  description?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  className = '',
  disabled = false,
  description,
}) => (
  <div className={`space-y-2 ${className}`}>
    <label className="flex items-start gap-3 cursor-pointer group">
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="sr-only"
          disabled={disabled}
        />
        <div
          className={`
          w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center
          ${
            checked
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 border-blue-500'
              : 'bg-white border-gray-300 group-hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        >
          {checked && <FaCheck className="w-3 h-3 text-white" />}
        </div>
      </div>
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
    </label>
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onSave?: () => void;
  saveText?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  saveText = 'Kaydet',
  size = 'md',
}) => {
  if (!isOpen) {
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" onClick={onClose} aria-label="Kapat" />
      <div
        className={`
        bg-white rounded-2xl w-full ${sizeClasses[size]} max-h-[81vh] 
        shadow-2xl border border-gray-100 overflow-hidden flex flex-col
        transform transition-all duration-300 scale-100 relative
      `}
      >
        {/* Header - Fixed */}
        <div className="relative bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">+</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/50 transition-colors"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="space-y-6">{children}</div>
          </div>
        </div>

        {/* Footer - Fixed */}
        {onSave && (
          <div className="bg-gray-50 px-6 py-5 border-t border-gray-100 flex gap-4 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-base"
            >
              İptal
            </button>
            <button
              onClick={onSave}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 text-base"
            >
              {saveText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
